import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { omit } from 'lodash';
import React from 'react';
import { Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import elements from 'core/config/elements';
import AddressType from 'core/enums/AddressType';
import { LocalizedMessage } from 'core/l10n/components';
import * as OvationsApi from 'core/ovations-api';
import AddressCertificationStatus from 'core/ovations-api/enums/AddressCertificationStatus';
import FAIcon from 'core/ovations-components/FAIcon';
import { isShallowEqual } from 'core/util/objects';

export interface AddressValidationModalProps {
    isOpen: boolean;
    address: OvationsApi.Types.Address;
    onCancel: () => void;
    onSave: (address: OvationsApi.Types.CertifiableAddress) => void;
    validateAddress: (address: OvationsApi.Types.Address) => Promise<OvationsApi.Types.AddressValidationResult | null>;
    modalSubtitle?: string;
}

export interface AddressValidationModalState {
    suggestedAddressAccepted: boolean;
    addressValidationResult: OvationsApi.Types.AddressValidationResult | undefined;
    isValidatingAddress: boolean;
}

class AddressValidationModal extends React.PureComponent<AddressValidationModalProps, AddressValidationModalState> {
    constructor(props: AddressValidationModalProps) {
        super(props);

        this.state = {
            suggestedAddressAccepted: false,
            addressValidationResult: undefined,
            isValidatingAddress: false,
        };
    }

    componentDidUpdate(prevProps: AddressValidationModalProps) {
        if (!prevProps.isOpen && this.props.isOpen) {
            this.validateAddress();
        }
    }

    getModalText() {
        if (this.state.isValidatingAddress) {
            return {
                title: <LocalizedMessage id="addressVerification_title" />,
                message: <LocalizedMessage id="addressVerification_validatingAddress" />,
            };
        }
        const { addressValidationResult } = this.state;

        let title;
        let message;
        let backButtonText;
        let continueButtonText;

        if (addressValidationResult) {
            if (addressValidationResult.suggestedAddress) {
                title = <LocalizedMessage id="addressVerification_title" />;
                message = <LocalizedMessage id="addressVerification_betterMatchFound" />;
                backButtonText = <LocalizedMessage id="addressVerification_action_back" />;
                continueButtonText = <LocalizedMessage id="addressVerification_action_continue" />;
            } else {
                title = (
                    <span>
                        <FAIcon icon={faExclamationTriangle} className="text-warning" />{' '}
                        <LocalizedMessage id="addressVerification_addressNotFound" />
                    </span>
                );
                message = <LocalizedMessage id="addressVerification_noMatchesFound" />;
                backButtonText = <LocalizedMessage id="addressVerification_action_edit" />;
                continueButtonText = <LocalizedMessage id="addressVerification_action_affirmContinue" />;
            }
        }

        return { title, message, backButtonText, continueButtonText };
    }

    validateAddress = async () => {
        const { address, validateAddress } = this.props;
        const { addressValidationResult } = this.state;
        const trimmedAddress = omit(address, 'status') as OvationsApi.Types.Address;

        // The input address was already validated
        if (addressValidationResult && isShallowEqual(trimmedAddress, addressValidationResult.originalAddress)) {
            if (addressValidationResult.isValid) {
                this.formatAddressAndSave();
            }
            return;
        }

        this.setState({ isValidatingAddress: true });
        const addressResult = await validateAddress(trimmedAddress);
        if (!addressResult) {
            return;
        }
        // The input address matches validation result exactly
        if (addressResult.isValid && addressResult.suggestedAddress === null) {
            this.setState({
                addressValidationResult: addressResult,
                isValidatingAddress: false,
            });
            this.formatAddressAndSave();

            // The input address is different from the validation result or no match was found
        } else {
            this.setState({
                addressValidationResult: addressResult,
                suggestedAddressAccepted: true,
                isValidatingAddress: false,
            });
        }
    };

    saveAddressChoice: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const { value } = e.currentTarget;
        const suggestedAddressAccepted = value === AddressType.SuggestedAddress;
        this.setState({ suggestedAddressAccepted });
    };

    formatAddressAndSave = () => {
        const { address, onSave } = this.props;
        const { addressValidationResult, suggestedAddressAccepted } = this.state;

        let formattedAddress: OvationsApi.Types.CertifiableAddress;

        if (addressValidationResult) {
            const { suggestedAddress } = addressValidationResult;

            if (suggestedAddress && suggestedAddressAccepted) {
                formattedAddress = {
                    address1: suggestedAddress.address1,
                    address2: suggestedAddress.address2 ? suggestedAddress.address2 : '',
                    city: suggestedAddress.city,
                    state: suggestedAddress.state,
                    zip: suggestedAddress.zip,
                    status: AddressCertificationStatus.Certified,
                    country: suggestedAddress.country,
                };
            } else {
                const certificationStatus = addressValidationResult.isValid
                    ? AddressCertificationStatus.Certified
                    : AddressCertificationStatus.NonCertified;

                formattedAddress = {
                    ...address,
                    status: certificationStatus,
                };
            }

            onSave(formattedAddress);
        }
    };

    renderAddress(address: OvationsApi.Types.Address | null) {
        if (address) {
            return (
                <div className="my-2">
                    {address.address1}
                    <br />
                    {address.address2}
                    {address.address2 ? <br /> : null}
                    {address.city}, {`${address.state} `}
                    <span className="text-nowrap">{address.zip}</span>
                    <br />
                    {address.country}
                </div>
            );
        }
    }

    renderOriginalAddress = (addressValidationResult: OvationsApi.Types.AddressValidationResult) => {
        const { suggestedAddress, originalAddress } = addressValidationResult;
        const columnClass = addressValidationResult.suggestedAddress ? 'col-sm-6' : 'col-sm-12  text-center';

        return (
            <div className={columnClass}>
                <FormGroup check>
                    {suggestedAddress && (
                        <Label htmlFor={elements.addressValidationModal.id.originalAddressPicker} check>
                            <Input
                                type="radio"
                                name="addressPicker"
                                id={elements.addressValidationModal.id.originalAddressPicker}
                                value={AddressType.OriginalAddress}
                                checked={!this.state.suggestedAddressAccepted}
                                onChange={this.saveAddressChoice}
                            />
                            <span className="fw-bold">
                                {' '}
                                <LocalizedMessage id="addressVerification_originalAddress" />
                            </span>
                        </Label>
                    )}
                    {this.renderAddress(originalAddress)}
                </FormGroup>
            </div>
        );
    };

    renderSuggestedAddress = (addressValidationResult: OvationsApi.Types.AddressValidationResult) => {
        const { suggestedAddress } = addressValidationResult;

        if (suggestedAddress) {
            return (
                <div className="col-sm-6">
                    <FormGroup check>
                        <Label htmlFor={elements.addressValidationModal.id.suggestedAddressPicker} check>
                            <Input
                                type="radio"
                                name="addressPicker"
                                id={elements.addressValidationModal.id.suggestedAddressPicker}
                                value={AddressType.SuggestedAddress}
                                checked={this.state.suggestedAddressAccepted}
                                onChange={this.saveAddressChoice}
                            />
                            <span className="fw-bold">
                                {' '}
                                <LocalizedMessage id="addressVerification_suggestedAddress" />
                            </span>
                        </Label>
                        {this.renderAddress(suggestedAddress)}
                    </FormGroup>
                </div>
            );
        }
    };

    render() {
        const { isOpen, onCancel, modalSubtitle } = this.props;
        const { addressValidationResult, isValidatingAddress } = this.state;

        const modalText = this.getModalText();
        return (
            <Modal isOpen={isOpen} className={elements.addressValidationModal.class.modal}>
                <ModalHeader tag="h5" toggle={onCancel}>
                    {modalText.title}
                    <span className="modal-header--subtitle">{modalSubtitle ? `(${modalSubtitle})` : null}</span>
                </ModalHeader>
                <ModalBody className="container">
                    <div className={elements.addressValidationModal.class.modalMessage}>{modalText.message}</div>
                    <div className="row my-2">
                        {!isValidatingAddress && addressValidationResult && (
                            <>
                                {this.renderSuggestedAddress(addressValidationResult)}
                                {this.renderOriginalAddress(addressValidationResult)}
                            </>
                        )}
                    </div>
                </ModalBody>
                <ModalFooter>
                    {modalText.backButtonText && (
                        <Button
                            id={elements.addressValidationModal.id.back}
                            color="secondary"
                            outline
                            onClick={onCancel}
                        >
                            {modalText.backButtonText}
                        </Button>
                    )}
                    {modalText.continueButtonText && (
                        <Button
                            onClick={this.formatAddressAndSave}
                            id={elements.addressValidationModal.id.continue}
                            color="primary"
                        >
                            {modalText.continueButtonText}
                        </Button>
                    )}
                </ModalFooter>
            </Modal>
        );
    }
}

export default AddressValidationModal;
